import React from 'react'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { Chip, Grid } from '@material-ui/core'
import dayjs from 'dayjs'
import AdminPage from '../../admin/shared/AdminPage'
import Loading from '../../../molecules/Loading'
import SydButton from '../../../commonDesign/Button'
import Card from '../../../molecules/Card'
import FlexRow from '../../../molecules/FlexRow'
import FlexActions from '../../../molecules/FlexActions'
import { useGetUnmanagedSchedule } from '../../../../api/scheduler'
import InfoRow from './InfoRow'
import { extractResourceName } from './utils'

function formatDate (value) {
  return value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014'
}

function UnmanagedScheduleDetail () {
  const { scheduleName } = useParams()
  const { url } = useRouteMatch()
  const history = useHistory()
  const { data: schedule, isLoading } = useGetUnmanagedSchedule(scheduleName)

  const backPath = url.replace(/\/[^/]+$/, '')

  if (isLoading || !schedule) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  const retryPolicy = schedule.target?.retryPolicy
  const deadLetterArn = schedule.target?.deadLetterConfig?.Arn ?? null
  const flexWindow = schedule.flexibleTimeWindow

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ width: '100%', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0 }}>{schedule.name}</h2>
              <Chip
                label={schedule.state === 'ENABLED' ? 'Enabled' : 'Disabled'}
                size='small'
                color={schedule.state === 'ENABLED' ? 'primary' : 'default'}
                style={{ marginTop: 4 }}
              />
            </div>
            <FlexActions style={{ flex: 1 }}>
              <SydButton size='sm' variant='ghost' onClick={() => history.push(backPath)}>
                Back
              </SydButton>
            </FlexActions>
          </FlexRow>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <InfoRow label='Expression'><code>{schedule.expression}</code></InfoRow>
            <InfoRow label='Timezone'>{schedule.timezone || '\u2014'}</InfoRow>
            <InfoRow label='State'>{schedule.state}</InfoRow>
            <InfoRow label='Flexible Time Window'>
              {flexWindow?.Mode === 'FLEXIBLE'
                ? `Flexible (${flexWindow.MaximumWindowInMinutes} min)`
                : flexWindow?.Mode || '\u2014'}
            </InfoRow>
            <InfoRow label='Start Date'>{schedule.startDate ? dayjs(schedule.startDate).format('MMM D, YYYY') : '\u2014'}</InfoRow>
            <InfoRow label='End Date'>{schedule.endDate ? dayjs(schedule.endDate).format('MMM D, YYYY') : '\u2014'}</InfoRow>
            <InfoRow label='Action After Completion'>{schedule.actionAfterCompletion || 'NONE'}</InfoRow>
            <InfoRow label='Created'>{formatDate(schedule.creationDate)}</InfoRow>
            <InfoRow label='Last Modified'>{formatDate(schedule.lastModificationDate)}</InfoRow>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <InfoRow label='Description'>{schedule.description || '\u2014'}</InfoRow>
            <InfoRow label='Target ARN'>
              <span title={schedule.target?.arn}>{extractResourceName(schedule.target?.arn) || '\u2014'}</span>
            </InfoRow>
            <InfoRow label='Target Role ARN'>
              <span title={schedule.target?.roleArn}>{extractResourceName(schedule.target?.roleArn) || '\u2014'}</span>
            </InfoRow>
            <InfoRow label='Target Input'>
              <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {schedule.target?.input || '\u2014'}
              </pre>
            </InfoRow>
            <InfoRow label='Retry Policy'>
              {retryPolicy
                ? `Max attempts: ${retryPolicy.MaximumRetryAttempts ?? '\u2014'}, Max age: ${retryPolicy.MaximumEventAgeInSeconds ?? '\u2014'}s`
                : '\u2014'}
            </InfoRow>
            <InfoRow label='Dead Letter Queue'>
              <span title={deadLetterArn}>{extractResourceName(deadLetterArn) || '\u2014'}</span>
            </InfoRow>
          </Card>
        </Grid>
      </Grid>
    </AdminPage>
  )
}

export default UnmanagedScheduleDetail
